/**
 * components/Chip/ChipRow
 * ──────────────────────────────────────────────────────────────────────────
 * A wrapping row of chips bound to one value — the replacement for every
 * dropdown in this product.
 *
 * Generic over the option's value type so a caller gets its own union back
 * from `onChange` rather than a bare string it has to re-narrow.
 *
 * The `required` flag renders the design's "· required" suffix on the label.
 * Validation itself is toast-based (utils/successHandler `showValidation`), so
 * this component never renders an error state of its own — an inline red
 * message under a chip row would move the layout as the user works.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { StyleSheet, Text, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { sans, size as fontSize } from '../../theme/typography';
import Chip, { type ChipShape } from './index';

export interface ChipOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
}

export interface ChipRowProps<T extends string> {
  /** Field label. Omit inside a filter bar, where the chips speak for themselves. */
  label?: string;
  options: readonly ChipOption<T>[];
  value: T | null | undefined;
  onChange: (value: T) => void;
  shape?: ChipShape;
  required?: boolean;
  capitalize?: boolean;
  /** A hint line under the label — used where a choice changes behaviour. */
  hint?: string;
}

export function ChipRow<T extends string>({
  label,
  options,
  value,
  onChange,
  shape = 'control',
  required = false,
  capitalize = false,
  hint,
}: ChipRowProps<T>) {
  return (
    <View style={styles.field}>
      {!!label && (
        <Text style={styles.label}>
          {label}
          {required && <Text style={styles.required}> · required</Text>}
        </Text>
      )}
      {!!hint && <Text style={styles.hint}>{hint}</Text>}
      <View style={styles.row}>
        {options.map((option) => (
          <Chip
            key={option.value}
            label={option.label}
            selected={value === option.value}
            disabled={option.disabled}
            shape={shape}
            capitalize={capitalize}
            onPress={() => onChange(option.value)}
          />
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  field: { gap: 7 },
  label: { ...sans('500'), fontSize: fontSize.label, color: neutral.ink2 },
  // "· required" is quieter than the label it qualifies — it marks the field,
  // it does not shout at it.
  required: { ...sans('400'), color: neutral.grey400 },
  hint: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey600 },
  row: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
});

export default ChipRow;
